import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createPortal } from 'react-dom';
import { X, Loader2, FolderPlus } from 'lucide-react';
import { useCreateProject } from '@/api/hooks/useProjects';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const projectSchema = z.object({
  name: z.string().min(1, 'Project name is required').max(100, 'Project name is too long'),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated?: () => void;
}

export default function CreateProjectModal({ isOpen, onClose, onProjectCreated }: CreateProjectModalProps) {
  const { t } = useTranslation();
  const createProjectMutation = useCreateProject();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  });

  if (!isOpen) return null;

  const onSubmit = async (data: ProjectFormData) => {
    try {
      await createProjectMutation.mutateAsync(data.name);
      toast.success(t('project.toast.created'));
      reset();
      onProjectCreated?.();
      onClose();
    } catch (error) {
      toast.error(t('project.toast.createError'));
      console.error(error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white border border-border rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-primary" />
            {t('project.newProject')}
          </h3>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('project.projectName')}
            </label>
            <input
              type="text"
              {...register('name')}
              placeholder={t('project.projectNamePlaceholder')}
              className="w-full bg-secondary border border-border rounded-xl py-3 px-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              autoFocus
            />
            {errors.name && (
              <p className="text-error text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createProjectMutation.isPending}
              className="px-6 py-2 rounded-xl text-sm font-bold bg-primary text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
            >
              {createProjectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {createProjectMutation.isPending ? t('project.creating') : t('project.createProject')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
